<script lang="ts">
	/**
	 * Which card one subscription is billed on, chosen by an admin.
	 *
	 * The customer's own controls move every subscription at once — making a card the default is what
	 * moves them — so this is the one place "bill *this* plan on *that* card" can be said. On a
	 * test-mode provider it also offers the provider's test cards, each applied in **one click**: the
	 * card is stored on the customer (or reused if it already is) and the subscription billed on it.
	 * Picking the card that declines and then advancing the customer's test clock past the renewal is
	 * how the whole failed-payment path is watched in seconds.
	 *
	 * A saved card on a **live** provider is behind a confirm, because the next real renewal is charged
	 * there; on a test provider it is one click like the test cards.
	 */

	import { createEventDispatcher, onMount } from 'svelte';
	import { Badge, Button, Modal } from 'flowbite-svelte';
	import { ConfirmButton, EmptyState, ModalHeader } from '../../../components';
	import {
		getSubscriptionPaymentMethods,
		setSubscriptionPaymentMethod,
		useSubscriptionTestCard
	} from '../../../AppNetwork';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import type { AdminPaymentMethod, SubscriptionPaymentMethods, TestCardPreset } from '../../../AppTypes';

	export let subscription: { uuid: string; productName: string };

	const dispatch = createEventDispatcher<{ close: void; changed: SubscriptionPaymentMethods }>();

	/** The dialog's own open state, so a dismissal reaches the parent as `close` (see `FormModal`). */
	let dialogOpen: boolean = true;
	$: if (!dialogOpen) dispatch('close');

	let options: SubscriptionPaymentMethods | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	/** The card or preset a request is in flight for. */
	let busyKey: string = '';

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			options = await getSubscriptionPaymentMethods(subscription.uuid);
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	function cardLabel(method: AdminPaymentMethod): string {
		const brand = method.brand === null ? 'Card' : method.brand.charAt(0).toUpperCase() + method.brand.slice(1);
		return method.last4 === null ? brand : `${brand} •••• ${method.last4}`;
	}

	function expiryOf(method: AdminPaymentMethod): string {
		return method.expiryMonth === null || method.expiryYear === null
			? ''
			: `${String(method.expiryMonth).padStart(2, '0')}/${method.expiryYear}`;
	}

	/** The test card the subscription is billed on now, recognised by its last four. */
	function isCurrentPreset(preset: TestCardPreset, current: SubscriptionPaymentMethods): boolean {
		const method = current.methods.find(candidate => candidate.uuid === current.currentPaymentMethodUUID);
		return method !== undefined && method.last4 === preset.last4 && method.brand?.toLowerCase() === preset.brand;
	}

	async function fnApply(key: string, work: () => Promise<SubscriptionPaymentMethods>, label: string): Promise<void> {
		busyKey = key;
		try {
			options = await work();
			toastSuccess(`${subscription.productName} now bills on ${label}.`);
			dispatch('changed', options);
		} catch (error) {
			toastError('The card was not changed.', (error as Error).message);
		} finally {
			busyKey = '';
		}
	}

	onMount(fnLoad);
</script>

<Modal bind:open={dialogOpen} size="md" outsideclose={busyKey === ''} dismissable={busyKey === ''}>
	<svelte:fragment slot="header">
		<ModalHeader title="Billing card" subtitle={subscription.productName} faIcon="credit-card" />
	</svelte:fragment>

	{#if loading}
		<EmptyState variant="loading" title="Loading cards…" />
	{:else if loadError.length > 0}
		<EmptyState variant="error" title="The cards could not be loaded." description={loadError}>
			<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
		</EmptyState>
	{:else if options !== null}
		{#if !options.canChange}
			<EmptyState title="The card cannot be changed here." description={options.reason ?? ''} />
		{:else}
			<div class="space-y-5">
				<section>
					<h3 class="card-label mb-2">Saved cards at {options.provider.name}</h3>
					{#if options.methods.length === 0}
						<p class="text-sm text-default">The customer has no card stored here.</p>
					{:else}
						<ul class="space-y-2">
							{#each options.methods as method (method.uuid)}
								<li class="widget bg-depth flex flex-wrap items-center gap-2" data-testid="saved-card">
									<span class="font-semibold">{cardLabel(method)}</span>
									{#if expiryOf(method) !== ''}
										<span class="text-xs text-default tabular-nums">{expiryOf(method)}</span>
									{/if}
									{#if method.nickname !== null}
										<span class="text-xs text-default">“{method.nickname}”</span>
									{/if}
									{#if method.isDefault}
										<Badge color="dark">Default</Badge>
									{/if}
									<span class="grow" />
									{#if method.uuid === options.currentPaymentMethodUUID}
										<Badge color="green">Billing this subscription</Badge>
									{:else}
										<ConfirmButton
											size="xs"
											color="alternative"
											disabled={busyKey !== ''}
											requireConfirmation={options.provider.mode === 'live'}
											confirmText={`Bill ${subscription.productName} on ${cardLabel(method)} from its next charge?`}
											on:confirm={() =>
												fnApply(method.uuid, () => setSubscriptionPaymentMethod(subscription.uuid, method.uuid), cardLabel(method))}
										>
											{#if busyKey === method.uuid}
												<i class="fa-solid fa-spinner fa-spin mr-1" />
											{/if}
											Bill on this card
										</ConfirmButton>
									{/if}
								</li>
							{/each}
						</ul>
					{/if}
				</section>

				{#if options.testCards.length > 0}
					<section>
						<h3 class="card-label mb-1">Test cards</h3>
						<p class="text-xs text-default mb-2">
							One click stores the card on the customer and bills this subscription on it. Then advance the
							customer's test clock past the renewal.
						</p>
						<div class="grid gap-2 sm:grid-cols-2">
							{#each options.testCards as preset (preset.key)}
								{@const current = isCurrentPreset(preset, options)}
								<button
									type="button"
									class="widget bg-depth depth-hover text-left disabled:opacity-60"
									class:ring-2={current}
									class:ring-green-500={current}
									disabled={busyKey !== '' || current}
									on:click={() => fnApply(preset.key, () => useSubscriptionTestCard(subscription.uuid, preset.key), preset.label)}
								>
									<span class="flex items-center gap-2 font-semibold">
										{#if busyKey === preset.key}
											<i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
										{:else}
											<i class="fa-solid fa-credit-card text-default" aria-hidden="true" />
										{/if}
										{preset.label}
										{#if current}<Badge color="green">Billing</Badge>{/if}
									</span>
									<span class="block text-xs text-default mt-1">{preset.description}</span>
								</button>
							{/each}
						</div>
					</section>
				{/if}
			</div>
		{/if}
	{/if}
</Modal>
